// gui/src/config/claude.ts
//
// This file is also the source of truth for the "Everyday"/"Smartest" tier
// constants (WHIZ_*/SMARTEST_*) even though "Everyday" no longer resolves to
// a Claude model — mirrors the backend's claude_config.py, which keeps the
// "expert" tier alias here too even though it now points at Gemini. Keeping
// both tiers' constants in one file (as originally set up) means every
// consumer keeps a single import line instead of two.

import { GEMINI_MODELS } from './gemini';

export const CLAUDE_MODELS = {
  // ✅ SUPPORTED MODELS (matches backend claude_config.py):
  // - genius (tier label "Smartest"): claude-opus-5 (all plans)
  // "expert" (tier label "Everyday") now resolves to Gemini 3.7 Flash — see
  // WHIZ_MODEL_ID below, defined in gemini.ts. Internal keys (expert/genius)
  // are kept stable for backward compatibility with persisted
  // threads/automations — only the underlying model/tier label changed.

  // Haiku 4.5 — kept for legacy alias resolution only (not shown in UI)
  HAIKU_4_5: 'claude-haiku-4-5',

  // Sonnet 5 — former Everyday default; kept selectable for parity / legacy pins.
  CLAUDE_SONNET_5: 'claude-sonnet-5',

  // Legacy Sonnet ID — kept only so old persisted model strings (saved threads,
  // localStorage, cached automations) still resolve. Resolves server-side via
  // backend resolve_claude_model / CLAUDE_MODEL_MAPPINGS.
  CLAUDE_SONNET_4_6: 'claude-sonnet-4-6',

  // Opus 5 — Smartest (all plans)
  CLAUDE_OPUS_5: 'claude-opus-5',

  // Legacy Opus IDs — kept only so old persisted model strings (saved threads,
  // automations, workflow steps) still compare equal to "Smartest" mode.
  // Resolves server-side via backend resolve_claude_model / CLAUDE_MODEL_MAPPINGS.
  CLAUDE_OPUS_4_8: 'claude-opus-4-8',
  CLAUDE_OPUS_4_7: 'claude-opus-4-7',

  // Sonnet 4.5 — kept for compatibility
  CLAUDE_SONNET_4_5: 'claude-sonnet-4-5',

  // Legacy aliases — kept for backward compatibility; resolve via backend resolve_claude_model
  CLAUDE_35_SONNET: 'claude-haiku-4-5',
  BEST: 'claude-haiku-4-5',
  FAST: 'claude-haiku-4-5',
  INSTANT: 'claude-haiku-4-5',
  LEGACY: 'claude-haiku-4-5',
};

// Legacy sentinel — used only to detect stale persisted Haiku spaces and upgrade them to Everyday.
export const DEFAULT_CLAUDE_MODEL = 'claude-haiku-4-5';

// ✅ Default model for all plans — Gemini 3.7 Flash (see gui/src/config/gemini.ts).
export const DEFAULT_EXPERT_MODEL = GEMINI_MODELS.GEMINI_3_7_FLASH;

// "Everyday" is the default, all-purpose tier. To swap the underlying model in
// the future, change ONLY GEMINI_MODELS.GEMINI_3_7_FLASH in gemini.ts (plus
// the matching backend alias in claude_config.py) — every UI surface that
// imports WHIZ_MODEL_ID / WHIZ_LABEL below picks up the change automatically.
export const WHIZ_MODEL_ID = DEFAULT_EXPERT_MODEL;
export const WHIZ_LABEL = 'Everyday';
export const WHIZ_MODEL_DISPLAY_NAME = 'Gemini 3.7 Flash';

// "Smartest" — deep-reasoning tier.
export const SMARTEST_MODEL_ID = CLAUDE_MODELS.CLAUDE_OPUS_5;
export const SMARTEST_LABEL = 'Smartest';
export const SMARTEST_MODEL_DISPLAY_NAME = 'Claude Opus 5';

// ✅ ONLY SUPPORTED MODELS - matches backend config
export const AVAILABLE_CLAUDE_MODELS = [
  { id: SMARTEST_MODEL_ID, name: `${SMARTEST_MODEL_DISPLAY_NAME} (${SMARTEST_LABEL})` },
];

// Combined list of all available models (Claude + other providers)
import { AVAILABLE_GROK_MODELS } from './grok';
import { AVAILABLE_DEEPSEEK_MODELS } from './deepseek';
import { AVAILABLE_OPENAI_MODELS } from './openai';
import { AVAILABLE_GEMINI_MODELS } from './gemini';

export const ALL_AVAILABLE_MODELS = [
  ...AVAILABLE_CLAUDE_MODELS,
  ...AVAILABLE_GROK_MODELS,
  ...AVAILABLE_DEEPSEEK_MODELS,
  ...AVAILABLE_OPENAI_MODELS,
  ...AVAILABLE_GEMINI_MODELS,
];

